/**
 * Markdown, rendered without ever handing a string to the browser as HTML.
 *
 * # Why this is hand-written and small
 *
 * A description or a comment is text written by somebody else, shown to
 * everybody who can read the repository. The usual pipeline — convert Markdown
 * to an HTML string, sanitise the string, inject it — has one safe outcome
 * and a long history of unsafe ones, because the sanitiser has to agree with
 * every browser's parser about what a string means.
 *
 * This component has no HTML string anywhere in it. It parses the text into
 * a small tree and returns React elements; every piece of the author's text
 * reaches the page as a text node, which the browser cannot interpret as
 * markup however it is spelled. There is no `dangerouslySetInnerHTML` to get
 * wrong, so `<script>`, an `onerror=` attribute or an unclosed tag in a
 * comment is shown as the characters that were typed.
 *
 * # What it understands
 *
 * Headings, paragraphs, bulleted and numbered lists, block quotes, fenced
 * code, rules; and inline, code, bold, italic, links, bare URLs, `@mentions`
 * and `issue #N` references. That is the Markdown people write in a review.
 * Raw HTML is not Markdown here: it is text.
 *
 * # What it deliberately does not do
 *
 * - **Images.** `![alt](url)` renders as a link. An image loads from wherever
 *   its author pointed it, the moment a reader opens the page, which makes
 *   every comment a way to learn who read it and from which address.
 * - **Any link scheme but http and https.** A `javascript:` or `data:` URL in
 *   a link is not made into a link at all.
 */

import { Link } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';

import styles from './Markdown.module.css';

/** The most text one render will parse. Bodies are bounded far below this by the API. */
const MAX_SOURCE = 100_000;

export interface MarkdownProps {
  source: string;
  /**
   * Where `issue #N` points, given the number. Without it a reference is
   * shown as text: this component does not guess which repository is meant.
   */
  issueHref?: (number: number) => string;
}

export function Markdown({ source, issueHref }: MarkdownProps) {
  const blocks = parseBlocks(source.slice(0, MAX_SOURCE));
  return (
    <div className={styles.markdown}>
      {blocks.map((block, index) => (
        <Fragment key={index}>{renderBlock(block, issueHref)}</Fragment>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * Blocks
 * ---------------------------------------------------------------------- */

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'code'; text: string }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'rule' };

const FENCE = /^\s{0,3}```/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/;
const BULLET = /^\s{0,3}[-*+]\s+(.*)$/;
const NUMBERED = /^\s{0,3}\d{1,9}[.)]\s+(.*)$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const RULE = /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;

export function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', lines: paragraph });
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';

    if (FENCE.test(line)) {
      flush();
      const body: string[] = [];
      i++;
      // To the closing fence, or to the end: an unclosed fence is code to the
      // end of the text rather than a block that swallows nothing.
      while (i < lines.length && !FENCE.test(lines[i] ?? '')) {
        body.push(lines[i] ?? '');
        i++;
      }
      blocks.push({ kind: 'code', text: body.join('\n') });
      continue;
    }
    if (line.trim() === '') {
      flush();
      continue;
    }
    if (RULE.test(line)) {
      flush();
      blocks.push({ kind: 'rule' });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({
        kind: 'heading',
        level: (heading[1] ?? '#').length,
        text: (heading[2] ?? '').replace(/\s+#+\s*$/, ''),
      });
      continue;
    }
    const quote = QUOTE.exec(line);
    if (quote) {
      flush();
      const body = [quote[1] ?? ''];
      while (i + 1 < lines.length) {
        const next = QUOTE.exec(lines[i + 1] ?? '');
        if (!next) break;
        body.push(next[1] ?? '');
        i++;
      }
      blocks.push({ kind: 'quote', lines: body });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet ?? numbered) {
      flush();
      const pattern = bullet ? BULLET : NUMBERED;
      const items = [(bullet ?? numbered)?.[1] ?? ''];
      while (i + 1 < lines.length) {
        const next = pattern.exec(lines[i + 1] ?? '');
        if (!next) break;
        items.push(next[1] ?? '');
        i++;
      }
      blocks.push({ kind: 'list', ordered: !bullet, items });
      continue;
    }
    paragraph.push(line);
  }
  flush();
  return blocks;
}

function renderBlock(block: Block, issueHref: MarkdownProps['issueHref']): ReactNode {
  switch (block.kind) {
    case 'heading': {
      // A comment's heading never outranks the page's own: the page owns h1
      // and h2, so the largest heading somebody can write is an h3.
      const level = Math.min(block.level + 2, 6);
      const content = renderInline(block.text, issueHref);
      if (level === 3) return <h3>{content}</h3>;
      if (level === 4) return <h4>{content}</h4>;
      if (level === 5) return <h5>{content}</h5>;
      return <h6>{content}</h6>;
    }
    case 'paragraph':
      return (
        <p>
          {block.lines.map((line, index) => (
            <Fragment key={index}>
              {index > 0 ? <br /> : null}
              {renderInline(line, issueHref)}
            </Fragment>
          ))}
        </p>
      );
    case 'code':
      return (
        <pre>
          <code>{block.text}</code>
        </pre>
      );
    case 'quote':
      return (
        <blockquote>
          {block.lines.map((line, index) => (
            <Fragment key={index}>
              {index > 0 ? <br /> : null}
              {renderInline(line, issueHref)}
            </Fragment>
          ))}
        </blockquote>
      );
    case 'list': {
      const items = block.items.map((item, index) => (
        <li key={index}>{renderInline(item, issueHref)}</li>
      ));
      return block.ordered ? <ol>{items}</ol> : <ul>{items}</ul>;
    }
    case 'rule':
      return <hr />;
  }
}

/* ---------------------------------------------------------------------- *
 * Inline
 * ---------------------------------------------------------------------- */

/**
 * One alternation, tried left to right at each position. Every branch is
 * built from negated character classes with no nested quantifiers, so the
 * match is linear in the length of the line whatever the line contains.
 *
 *   1 code   2 bold   3 italic   4 link text  5 link target
 *   6 bare URL   7 mention   8 issue number
 */
const INLINE =
  /`([^`\n]+)`|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|\[([^\]\n]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>()[\]]+)|(?<![\w@/.-])@([A-Za-z0-9](?:-?[A-Za-z0-9]){0,38})|\bissue #(\d{1,9})\b/gi;

/**
 * A link target somebody wrote, or `null` if it is not one this renders.
 *
 * Parsed by the platform's URL parser and accepted only as `http:` or
 * `https:`. Checking the scheme of the parsed URL — not the start of the
 * string — is what refuses `javascript:` however it is capitalised, padded or
 * encoded.
 */
export function safeHref(target: string): string | null {
  let url: URL;
  try {
    url = new URL(target);
  } catch {
    return null;
  }
  return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
}

function external(href: string, children: ReactNode, key: number): ReactNode {
  return (
    <a key={key} href={href} rel="noopener noreferrer nofollow" target="_blank">
      {children}
    </a>
  );
}

function renderInline(text: string, issueHref: MarkdownProps['issueHref'], depth = 0): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  INLINE.lastIndex = 0;
  // The regular expression is shared and stateful, so the matches are
  // collected before anything recurses into it.
  const matches = [...text.matchAll(INLINE)];

  for (const match of matches) {
    const start = match.index;
    if (start > last) out.push(text.slice(last, start));
    last = start + match[0].length;
    const [whole, code, bold, italic, linkText, linkTarget, bare, mention, issue] = match;

    if (code !== undefined) {
      out.push(<code key={key++}>{code}</code>);
    } else if (bold !== undefined) {
      out.push(
        <strong key={key++}>{depth < 2 ? renderInline(bold, issueHref, depth + 1) : bold}</strong>,
      );
    } else if (italic !== undefined) {
      out.push(
        <em key={key++}>{depth < 2 ? renderInline(italic, issueHref, depth + 1) : italic}</em>,
      );
    } else if (linkText !== undefined && linkTarget !== undefined) {
      const href = safeHref(linkTarget);
      // Not a link this renders: shown exactly as it was typed.
      out.push(href ? external(href, linkText, key++) : whole);
    } else if (bare !== undefined) {
      // Trailing punctuation belongs to the sentence, not to the address.
      const trimmed = bare.replace(/[.,;:!?'"]+$/, '');
      const href = safeHref(trimmed);
      out.push(href ? external(href, trimmed, key++) : trimmed);
      if (trimmed.length < bare.length) out.push(bare.slice(trimmed.length));
    } else if (mention !== undefined) {
      out.push(
        <span key={key++} className={styles.mention}>
          @{mention}
        </span>,
      );
    } else if (issue !== undefined) {
      const number = Number(issue);
      out.push(
        issueHref ? (
          <Link key={key++} to={issueHref(number)}>
            {whole}
          </Link>
        ) : (
          whole
        ),
      );
    }
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
